import {
  addMonths,
  differenceInMonths,
  endOfYear,
  isAfter,
  isBefore,
  startOfMonth,
  startOfYear,
} from 'date-fns'

import { getEndOfMonthDay } from '@enechain/eposition-utils'

// ref: https://docs.google.com/spreadsheets/d/1DistL4P_TuBdVBSxVFN7d-ZMgHDiYj-sfT2fUuo8lg4/edit?gid=1211885931#gid=1211885931
export const MAX_MONTH_OFFSET = 59

export function getMinDeliveryDate(now?: Date): Date {
  const base = now ?? new Date()
  return addMonths(startOfMonth(base), 1)
}
// ref: https://www.notion.so/enechain/eClear-EEX-1c231f880991803e894eda80b6b861cc?source=copy_link#1d031f88099180a0b0e8e1bf1805decc
export function getMaxDeliveryDate(maxMonthOffset: number, now?: Date): Date {
  const base = now ?? new Date()
  return addMonths(startOfMonth(base), maxMonthOffset)
}

const END_QUARTER_MONTHS_RANGE = 2 // 四半期の末月までの月数
const NEXT_QUARTER_MONTHS_RANGE = 3 // 次の四半期の開始月までの月数
const END_SEASON_MONTHS_RANGE = 5 // シーズンの末月までの月数
const NEXT_SEASON_MONTHS_RANGE = 6 // 次のシーズンの開始月までの月数
const FINANCIAL_YEAR_MONTHS_RANGE_FROM_JANUARY = 3
const END_FISCAL_YEAR_MONTHS_RANGE = 11 // 会計年度の末月までの月数
const NEXT_FISCAL_YEAR_MONTHS_RANGE = 12 // 次の会計年度の開始月までの月数

export function getSelectableQuarterDates(
  maxMonthOffset: number,
  now?: Date,
): Date[] {
  const base = now ?? new Date()
  const startYearDate = startOfYear(base)
  const selectableDates: Date[] = []
  const minDeliveryDate = getMinDeliveryDate(base)
  const maxDeliveryDate = getMaxDeliveryDate(maxMonthOffset, base)
  const maxMonthsFromYearStart = differenceInMonths(
    maxDeliveryDate,
    startYearDate,
  )

  for (let i = 0; i <= maxMonthsFromYearStart; i += NEXT_QUARTER_MONTHS_RANGE) {
    const currentMonthDate = addMonths(startYearDate, i)
    if (isBefore(currentMonthDate, minDeliveryDate)) {
      continue
    }
    if (
      isAfter(
        addMonths(startYearDate, i + END_QUARTER_MONTHS_RANGE),
        maxDeliveryDate,
      )
    ) {
      break
    }
    selectableDates.push(currentMonthDate)
  }

  return selectableDates
}

export function getQuarterDateRange(
  date: Date | undefined,
): { deliveryStartDate: Date; deliveryEndDate: Date } | undefined {
  if (date === undefined) {
    return undefined
  }

  const startMonthDate = startOfMonth(date)

  return {
    deliveryStartDate: startMonthDate,
    deliveryEndDate: getEndOfMonthDay(
      addMonths(startMonthDate, END_QUARTER_MONTHS_RANGE),
    ),
  }
}

export function getSelectableSeasonDates(
  maxMonthOffset: number,
  now?: Date,
): Date[] {
  const base = now ?? new Date()
  const startSeasonDate = addMonths(
    startOfYear(base),
    FINANCIAL_YEAR_MONTHS_RANGE_FROM_JANUARY,
  )
  const selectableDates: Date[] = []
  const minDeliveryDate = getMinDeliveryDate(base)
  const maxDeliveryDate = getMaxDeliveryDate(maxMonthOffset, base)
  const maxMonthsFromSeasonStart = differenceInMonths(
    maxDeliveryDate,
    startSeasonDate,
  )

  for (
    let i = 0;
    i <= maxMonthsFromSeasonStart;
    i += NEXT_SEASON_MONTHS_RANGE
  ) {
    const currentMonthDate = addMonths(startSeasonDate, i)
    if (isBefore(currentMonthDate, minDeliveryDate)) {
      continue
    }
    if (
      isAfter(
        addMonths(startSeasonDate, i + END_SEASON_MONTHS_RANGE),
        maxDeliveryDate,
      )
    ) {
      break
    }
    selectableDates.push(addMonths(startSeasonDate, i))
  }

  return selectableDates
}

export function getSeasonDateRange(
  date: Date | undefined,
): { deliveryStartDate: Date; deliveryEndDate: Date } | undefined {
  if (date === undefined) {
    return undefined
  }

  const startMonthDate = startOfMonth(date)

  return {
    deliveryStartDate: startMonthDate,
    deliveryEndDate: getEndOfMonthDay(
      addMonths(startMonthDate, END_SEASON_MONTHS_RANGE),
    ),
  }
}

export function getSelectableFiscalYearDates(
  maxMonthOffset: number,
  now?: Date,
): Date[] {
  const base = now ?? new Date()
  const startFiscalYearDate = addMonths(
    startOfYear(base),
    FINANCIAL_YEAR_MONTHS_RANGE_FROM_JANUARY,
  )
  const selectableDates: Date[] = []
  const minDeliveryDate = getMinDeliveryDate(base)
  const maxDeliveryDate = getMaxDeliveryDate(maxMonthOffset, base)
  const maxMonthsFromFiscalYearStart = differenceInMonths(
    maxDeliveryDate,
    startFiscalYearDate,
  )

  for (
    let i = 0;
    i <= maxMonthsFromFiscalYearStart;
    i += NEXT_FISCAL_YEAR_MONTHS_RANGE
  ) {
    const currentMonthDate = addMonths(startFiscalYearDate, i)
    if (isBefore(currentMonthDate, minDeliveryDate)) {
      continue
    }
    if (
      isAfter(
        addMonths(startFiscalYearDate, i + END_FISCAL_YEAR_MONTHS_RANGE),
        maxDeliveryDate,
      )
    ) {
      break
    }
    selectableDates.push(addMonths(startFiscalYearDate, i))
  }

  return selectableDates
}

export function getFiscalYearDateRange(date: Date | undefined): {
  deliveryStartDate: Date | undefined
  deliveryEndDate: Date | undefined
} {
  const startYearDate =
    date !== undefined
      ? addMonths(startOfYear(date), FINANCIAL_YEAR_MONTHS_RANGE_FROM_JANUARY)
      : undefined
  const endYearDate =
    date !== undefined
      ? getEndOfMonthDay(
          addMonths(endOfYear(date), FINANCIAL_YEAR_MONTHS_RANGE_FROM_JANUARY),
        )
      : undefined

  return {
    deliveryStartDate: startYearDate,
    deliveryEndDate: endYearDate,
  }
}
